import type { ComponentType } from 'react'
import type { RouteDef } from './routes'
import type { RouteSeo } from './lib/seo'
import { STORY_SLUG } from './components/pr7536-story/route'
import { ArticlePage } from './pages/ArticlePage'
import { CustomerStoryPage } from './pages/CustomerStoryPage'
import { OffScriptInteractive } from './components/blog/OffScriptInteractive'
import { LegalPage } from './pages/LegalPage'
import { PaperPage } from './pages/PaperPage'
import { AuthorPage } from './pages/AuthorPage'
import { SITE_URL, SITE_NAME, absoluteUrl } from './lib/site'
import {
  blogPosts,
  changelogEntries,
  customerStories,
  legalPages,
  researchPapers,
  contentPath,
  preloadContent,
  resolvedContent,
  type ContentEntry,
} from './lib/content'
import { allAuthorsWithPosts, postsByAuthor, authorSlugPath } from './lib/authors'

/** Per-slug `afterContent` overrides (see ArticlePage's `afterContent` prop
 * doc) for the rare post that needs something beyond clean MDX — every other
 * post leaves this unset and renders exactly as before. */
const BLOG_AFTER_CONTENT: Record<string, ComponentType> = {
  'we-red-teamed-our-own-ai-agent-and-found-14-real-bugs': OffScriptInteractive,
}

/** Full-page blog overrides retain the normal content entry and article SEO.
 * Dynamic imports keep story copy and scroll code off unrelated routes. */
const BLOG_CUSTOM_PAGES: Record<string, Pick<RouteDef, 'load' | 'chunkSrc'>> = {
  [STORY_SLUG]: {
    load: () => import('./components/pr7536-story/StoryPage').then((module) => module.StoryPage),
    chunkSrc: 'src/components/pr7536-story/StoryPage.tsx',
  },
}

const BACK = {
  blog: { href: '/blog', label: 'Blog' },
  changelog: { href: '/changelog', label: 'Changelog' },
  customers: { href: '/customers', label: 'Customer stories' },
} as const

/** Clamp a description into the 50-160 char window the SEO audit enforces. */
function clampDescription(e: ContentEntry): string {
  let base = (e.meta.seoDescription || e.meta.summary || e.meta.intro || e.meta.title)
    .replace(/\s+/g, ' ')
    .trim()
  if (base.length > 158) base = base.slice(0, 157).replace(/\s+\S*$/, '') + '…'
  const pad =
    e.collection === 'changelog'
      ? ' A LangWatch product update.'
      : e.collection === 'customers'
        ? ' A LangWatch customer story.'
        : ' Read more on the LangWatch blog.'
  while (base.length < 50) base = (base + pad).slice(0, 158)
  return base
}

function articleSeo(e: ContentEntry): RouteSeo {
  const url = `${SITE_URL}${contentPath(e)}`
  // A few features have both a blog announcement and a changelog entry with the
  // same title; qualify changelog titles so every page title stays unique.
  const fullTitle = e.collection === 'changelog' ? `${e.meta.title} | Changelog` : e.meta.title
  // Prefer a hand-written concise SEO title when present; the H1 keeps meta.title.
  const title = e.meta.seoTitle ?? fullTitle
  return {
    title,
    description: e.collection === 'blog' && BLOG_CUSTOM_PAGES[e.slug] && e.meta.seoDescription
      ? e.meta.seoDescription
      : clampDescription(e),
    image: e.meta.image ?? e.meta.card,
    type: 'article',
    // machine-readable dates and author in the head (article:* OG tags)
    ...(e.meta.date ? { publishedTime: e.meta.date } : {}),
    ...(e.meta.updated ? { modifiedTime: e.meta.updated } : {}),
    ...(e.meta.author ? { articleAuthor: e.meta.author } : {}),
    // long-form editorial: judged by the full Yoast article profile
    contentType: 'article',
    // posts that declare a target keyphrase get scored by content:check
    keyphrase: e.meta.keyphrase,
    // syndicated content: canonical points at the off-site original
    ...(e.meta.canonicalUrl ? { canonicalUrl: e.meta.canonicalUrl } : {}),
    // blog posts ship a machine-readable markdown variant at /blog/<slug>.md
    // (emitted by scripts/gen-blog-markdown.mjs), advertised via
    // <link rel="alternate" type="text/markdown">
    ...(e.collection === 'blog' ? { markdownPath: `${contentPath(e)}.md` } : {}),
    // blog and changelog pages advertise their section's RSS feed (emitted by
    // scripts/gen-feeds.mjs) so monitoring tools autodiscover it from any page
    ...(e.collection === 'blog'
      ? { feed: { href: '/blog/rss.xml', title: 'LangWatch Blog' } }
      : e.collection === 'changelog'
        ? { feed: { href: '/changelog/rss.xml', title: 'LangWatch Changelog' } }
        : {}),
    jsonLd: [
      {
        '@context': 'https://schema.org',
        // BlogPosting (an Article subtype) for blog posts: the more specific
        // type is what blog-monitoring tools and rich results key on.
        '@type': e.collection === 'blog' ? 'BlogPosting' : 'Article',
        headline: e.meta.title,
        ...(e.meta.date ? { datePublished: e.meta.date } : {}),
        ...(e.meta.updated ?? e.meta.date
          ? { dateModified: e.meta.updated ?? e.meta.date }
          : {}),
        ...(e.meta.author ? { author: { '@type': 'Person', name: e.meta.author } } : {}),
        ...(e.meta.image ? { image: e.meta.image } : {}),
        url,
      },
      // FAQPage schema when the post ships an end-of-article FAQ (GEO/AEO).
      ...(e.meta.faq?.length
        ? [
            {
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: e.meta.faq.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            },
          ]
        : []),
    ],
  }
}

function detailRoute(e: ContentEntry): RouteDef {
  const back = BACK[e.collection]
  const customPage = e.collection === 'blog' ? BLOG_CUSTOM_PAGES[e.slug] : undefined
  // Customer stories render through the dedicated CustomerStoryPage template;
  // blog and changelog keep the shared ArticlePage.
  const Page: ComponentType =
    e.collection === 'customers'
      ? () => <CustomerStoryPage entry={e} Content={resolvedContent('customers', e.slug)} />
      : () => (
          <ArticlePage
            entry={e}
            Content={resolvedContent(e.collection, e.slug)}
            backHref={back.href}
            backLabel={back.label}
            afterContent={e.collection === 'blog' ? BLOG_AFTER_CONTENT[e.slug] : undefined}
          />
        )
  return {
    path: contentPath(e),
    ...(customPage ?? { Component: Page }),
    seo: articleSeo(e),
    ...(customPage ? {} : { preload: () => preloadContent(e.collection, e.slug) }),
    // Old slugs (post-rename) resolve here and prerender with a canonical
    // pointing at the new path; a public/_redirects rule 301s them at the edge.
    ...(e.meta.aliases?.length ? { aliases: e.meta.aliases } : {}),
    // Pages whose canonical lives off-site stay linkable but are not listed
    // in the sitemap: a sitemap should only carry canonical URLs.
    ...(e.meta.canonicalUrl ? { sitemap: false } : {}),
  }
}

const LEGAL_DESCRIPTIONS: Record<string, string> = {
  'privacy-policy':
    'How LangWatch (Reasoning Engine B.V.) collects, uses, stores, and protects your information when you use our website and services.',
  'terms-conditions':
    'The terms of service that govern your use of LangWatch, the AI agent engineering, evaluation, and observability platform.',
}

/** Standalone legal pages (privacy, terms), rendered with the minimal profile. */
function legalRoutes(): RouteDef[] {
  return legalPages().map(({ slug, meta }) => ({
    path: `/legal/${slug}`,
    Component: () => <LegalPage Content={resolvedContent('legal', slug)} />,
    seo: {
      title: `${meta.title} | LangWatch`,
      description: LEGAL_DESCRIPTIONS[slug] ?? `${meta.title} for LangWatch.`,
      contentType: 'minimal',
    },
    preload: () => preloadContent('legal', slug),
  }))
}

/** Research papers (src/content/research), rendered with the arXiv-style
 * PaperPage rather than the blog template. */
function researchRoutes(): RouteDef[] {
  return researchPapers().map(({ slug, meta }) => ({
    path: `/research/${slug}`,
    Component: () => (
      <PaperPage slug={slug} meta={meta} Content={resolvedContent('research', slug)} />
    ),
    seo: {
      title: `${meta.seoTitle ?? meta.title} | LangWatch`,
      description: (meta.seoDescription ?? meta.intro ?? meta.title).slice(0, 158),
      image: meta.image,
      type: 'article',
      keyphrase: meta.keyphrase,
      contentType: 'article',
      jsonLd: [
        {
          '@context': 'https://schema.org',
          // ScholarlyArticle rather than the blog's BlogPosting: it is the
          // type academic indexers key on, and it is the only Article subtype
          // carrying `abstract`.
          '@type': 'ScholarlyArticle',
          headline: meta.title,
          ...(meta.abstract ? { abstract: meta.abstract } : {}),
          ...(meta.date ? { datePublished: meta.date } : {}),
          author: (meta.authors?.length ? meta.authors : [meta.author].filter(Boolean)).map(
            (name) => ({ '@type': 'Person', name }),
          ),
          publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
          ...(meta.image ? { image: absoluteUrl(meta.image) } : {}),
          ...(meta.keyphrase ? { keywords: meta.keyphrase } : {}),
          isAccessibleForFree: true,
          url: `${SITE_URL}/research/${slug}`,
          mainEntityOfPage: `${SITE_URL}/research/${slug}`,
        },
      ],
    },
    preload: () => preloadContent('research', slug),
  }))
}

/** Per-author bio + post-archive pages, one per author with >=1 blog post. */
function authorRoutes(): RouteDef[] {
  return allAuthorsWithPosts().map((author) => {
    const count = postsByAuthor(author.slug).length
    const roleClause = author.role ? `, ${author.role} at LangWatch` : ' at LangWatch'
    return {
      path: authorSlugPath(author),
      Component: () => <AuthorPage slug={author.slug} />,
      seo: {
        feed: { href: '/blog/rss.xml', title: 'LangWatch Blog' },
        title: `${author.name} | LangWatch Blog`,
        description:
          `${count} ${count === 1 ? 'post' : 'posts'} by ${author.name}${roleClause} on ` +
          'testing, evaluating, and observing AI agents in production.',
        contentType: 'minimal',
      },
    }
  })
}

/** All generated content routes: blog/changelog/customer details + legal + author pages. */
export function contentRoutes(): RouteDef[] {
  const entries = [...blogPosts(), ...changelogEntries(), ...customerStories()]
  const usedDesc = new Set<string>()
  const details = entries.map((e) => {
    const route = detailRoute(e)
    let desc = route.seo.description ?? ''
    if (usedDesc.has(desc)) {
      // A few features have both a blog post and a changelog entry (same intro);
      // disambiguate the duplicate with the page's unique title, kept under 160.
      const head = desc.slice(0, Math.max(40, 150 - e.meta.title.length)).replace(/\s+\S*$/, '')
      desc = `${head} | ${e.meta.title}`.slice(0, 158)
      let n = 2
      while (usedDesc.has(desc)) desc = `${head} | ${e.meta.title} (${n++})`.slice(0, 158)
      route.seo = { ...route.seo, description: desc }
    }
    usedDesc.add(desc)
    return route
  })
  return [...details, ...legalRoutes(), ...researchRoutes(), ...authorRoutes()]
}
